import React from 'react';
import { ActivityIndicator, View } from 'react-native';
import { errorMessage } from '../lib/errors';
import { Button } from './Button';
import { Icon, type IconName } from './Icon';
import { Text } from './Text';
import { useTheme } from './theme';

export function EmptyState({ icon = 'inbox', title, description, action }: { icon?: IconName; title: string; description?: string; action?: React.ReactNode }) {
  const { colors } = useTheme();
  return (
    <View style={{ alignItems: 'center', justifyContent: 'center', padding: 32, gap: 8 }}>
      <View style={{ width: 48, height: 48, borderRadius: 14, backgroundColor: colors.primarySoft, alignItems: 'center', justifyContent: 'center', marginBottom: 4 }}>
        <Icon name={icon} size={22} color={colors.primary} />
      </View>
      <Text variant="title" align="center">
        {title}
      </Text>
      {!!description && (
        <Text variant="muted" align="center" style={{ maxWidth: 420 }}>
          {description}
        </Text>
      )}
      {action ? <View style={{ marginTop: 8 }}>{action}</View> : null}
    </View>
  );
}

export function Loading({ label, inline }: { label?: string; inline?: boolean }) {
  const { colors } = useTheme();
  return (
    <View accessibilityRole="progressbar" accessibilityLabel={label ?? 'Loading'} style={inline ? { flexDirection: 'row', gap: 8, alignItems: 'center' } : { padding: 32, alignItems: 'center', justifyContent: 'center', gap: 10 }}>
      <ActivityIndicator color={colors.primary} />
      {!!label && <Text variant="muted">{label}</Text>}
    </View>
  );
}

export function ErrorState({ error, onRetry, title = 'Something went wrong' }: { error: unknown; onRetry?: () => void; title?: string }) {
  return <EmptyState icon="alert" title={title} description={errorMessage(error)} action={onRetry ? <Button title="Try again" icon="refresh" onPress={onRetry} /> : undefined} />;
}

/**
 * Renders loading / error / children for a react-query result.
 * `<QueryState query={q}>{(data) => ...}</QueryState>`
 */
export function QueryState<T>({ query, children, empty }: { query: { data: T | undefined; isLoading: boolean; isError: boolean; error: unknown; refetch: () => unknown }; children: (data: T) => React.ReactNode; empty?: React.ReactNode }) {
  if (query.isLoading) return <Loading />;
  if (query.isError) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;
  if (query.data === undefined || (Array.isArray(query.data) && !query.data.length && empty)) return <>{empty ?? null}</>;
  return <>{children(query.data)}</>;
}
